import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { SquarePen, CheckCircle, Ban, Plus, RefreshCw } from "lucide-react";
import "./Technologies.css";

import Pagination from "../../app/components/Pagination/Pagination";
import LoadingSpinner from "../../app/components/Loader/LoadingSpinner";

import { getCategory, addNewCategory, updateCategory, ChangeCategoryStatus } from "../../api/Category API/category";
import {
  getTechnologiesByCategory,
  addNewTechnologies,
  ChangeTechnologiesStatus,
  updateTechnologies,
} from "../../api/Category Technology API/categoryTechnologies";
import { RoleId } from "../../app/Enums/RoleId";

function Technologies() {
  /* ================= STATES ================= */

  const [categories, setCategories] = useState([]);
  const [technologies, setTechnologies] = useState([]);

  const [categoryName, setCategoryName] = useState("");
  const [technologyName, setTechnologyName] = useState("");

  const [selectedCategory, setSelectedCategory] = useState(null);

  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isTechnologyModalOpen, setIsTechnologyModalOpen] = useState(false);

  const [isEditCategory, setIsEditCategory] = useState(false);
  const [isEditTechnology, setIsEditTechnology] = useState(false);

  const [editTechnologyId, setEditTechnologyId] = useState(null);

  const [pageLoading, setPageLoading] = useState(true);
  const [techLoading, setTechLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [totalRows, setTotalRows] = useState(0);

  const roleId = Number(localStorage.getItem("RoleId"));

  const [filters, setFilters] = useState({
    PageNo: 1,
    PageSize: 10,
    categoryId: null,
  });

  const totalPages = Math.ceil(totalRows / filters.PageSize);

  /* ================= FETCH CATEGORY ================= */

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setPageLoading(true);

    const payload = {
      userRole: roleId,
    };

    try {
      const res = await getCategory(payload);

      if (res.result === 1) {
        const data = res.result_value || [];

        setCategories(data);

        if (data.length > 0) {

          let currentCategoryId = filters.categoryId;

          // if no category selected yet
          if (!currentCategoryId) {
            currentCategoryId = data[0].categoryId;
          }

          // find current category from updated list
          const currentCategory = data.find(
            (c) => c.categoryId === currentCategoryId
          );

          setSelectedCategory(currentCategory);

          setFilters((prev) => ({
            ...prev,
            categoryId: currentCategoryId,
          }));
        }
      } else {
        toast.error("Failed to load categories");
      }
    } catch {
      toast.error("Error fetching categories");
    } finally {
      setPageLoading(false);
    }
  };

  /* ================= FETCH TECHNOLOGIES ================= */

  useEffect(() => {
    if (!filters.categoryId) return;

    fetchTechnologies(filters);
  }, [filters.categoryId, filters.PageNo, filters.PageSize]);

  const fetchTechnologies = async (payload) => {
    setTechLoading(true);

    try {
      const res = await getTechnologiesByCategory(payload);

      if (res.result === 1) {
        setTechnologies(res.result_value.technologies || []);
        setTotalRows(res.result_value.totalRows || 0);
      } else {
        toast.error("Failed to load technologies");
      }
    } catch {
      toast.error("Error fetching technologies");
    } finally {
      setTechLoading(false);
    }
  };

  /* ================= CATEGORY CHANGE ================= */

  const handleCategoryChange = (e) => {
    const id = Number(e.target.value);

    const cat = categories.find((c) => c.categoryId === id);

    setSelectedCategory(cat);

    setFilters((prev) => ({
      ...prev,
      categoryId: id,
      PageNo: 1,
    }));
  };

  /* ================= ADD CATEGORY ================= */

  const handleAddCategory = async () => {
    if (!categoryName.trim()) return toast.error("Category name required");

    const payload = [
      {
        categoryName: categoryName,
        createdBy: roleId,
      },
    ];
    setProcessing(true);
    try {
      const res = await addNewCategory(payload);

      if (res.result === 1) {
        toast.success("Category added successfully");

        setIsCategoryModalOpen(false);
        setCategoryName("");

        fetchCategories();
      }
    } catch {
      toast.error("Error adding category");
    } finally {
      setProcessing(false);
    }
  };

  /* ================= EDIT CATEGORY ================= */

  const handleEditCategory = () => {
    setCategoryName(selectedCategory.categoryName);
    setIsEditCategory(true);
    setIsCategoryModalOpen(true);
  };


  const handleUpdateCategory = async () => {
    if (!categoryName.trim()) return toast.error("Category name required");

    const payload = {
      categoryId: selectedCategory.categoryId,
      categoryName: categoryName,
      modifiedBy: roleId,
    }
    setProcessing(true);
    try {
      var res = await updateCategory(payload);
      if (res.result === 1) {
        toast.success("Category updated successfully");
        setIsEditCategory(false);
        setIsCategoryModalOpen(false);
        fetchCategories();
      } else {
        toast.error("Failed to update category");
      }
    }
    catch (err) {

    } finally {
      setProcessing(false);
      setCategoryName("");
    }

  }


  const handleChangeVisibilityForCategories = async () => {
    const payload = {
      categoryId: selectedCategory.categoryId,
      isActive: !selectedCategory.isActive,
      modifiedBy: roleId,
    };

    try {
      const res = await ChangeCategoryStatus(payload);

      if (res.result === 1) {
        toast.success("Status updated");
        fetchCategories();
      }
    } catch {
      toast.error("Error updating status");
    }
  };
  /* ================= ADD / EDIT TECHNOLOGY ================= */

  const handleAddTechnology = async () => {
    if (!technologyName.trim()) return toast.error("Technology name required");

    const payload = [
      {
        technologyName: technologyName,
        categoryId: filters.categoryId,
        createdBy: roleId,
      },
    ];
    setProcessing(true);
    try {
      const res = await addNewTechnologies(payload);

      if (res.result === 1) {
        toast.success("Technology saved successfully");

        setTechnologyName("");
        setIsTechnologyModalOpen(false);

        fetchTechnologies(filters);
      }
    } catch {
      toast.error("Error saving technology");
    } finally {
      setProcessing(false);
    }
  };

  const handleEditTechnology = (tech) => {
    setTechnologyName(tech.technologyName);
    setEditTechnologyId(tech.technologyId);

    setIsEditTechnology(true);
    setIsTechnologyModalOpen(true);
  };

  const handleUpdateTechnology = async () => {
    if (!technologyName.trim()) return toast.error("Technology name required");
    setProcessing(true);
    try {
      const payload = {
        technologyId: editTechnologyId,
        technologyName: technologyName,
        modifiedBy: roleId,
      };

      var result = await updateTechnologies(payload);

      if (result.result === 1) {
        toast.success("Technology updated successfully");
        setIsEditTechnology(false);
        setIsTechnologyModalOpen(false);
        fetchTechnologies(filters);
      }
    } catch (error) {
      toast.error("Error updating technology");
    } finally {
      setProcessing(false);
      setTechnologyName("");
    }
  };
  /* ================= TECHNOLOGY STATUS ================= */

  const handleChangeVisibilityForTechnologies = async (tech) => {
    const payload = {
      technologyId: tech.technologyId,
      isActive: !tech.isActive,
      modifiedBy: roleId,
    };

    try {
      const res = await ChangeTechnologiesStatus(payload);

      if (res.result === 1) {
        toast.success("Status updated");
        fetchTechnologies(filters);
      }
    } catch {
      toast.error("Error updating status");
    }
  };

  /* ================= RESET MODAL ================= */

  const closeTechnologyModal = () => {
    setTechnologyName("");
    setIsEditTechnology(false);
    setIsTechnologyModalOpen(false);
  };

  const closeCategoryModal = () => {
    setCategoryName("");
    setIsEditCategory(false);
    setIsCategoryModalOpen(false);
  };

  /* ================= UI ================= */

  return (
    <>
      {pageLoading ? (
        <LoadingSpinner />
      ) : (
        <div className="main-out-wraper">
          {/* HEADER */}

          <div className="role-box">
            <h2>Technology Management</h2>

            <div className="top-bar">
              <div className="technolgy-bx">
                <div className="form-wraper">
                  <label>Select Category</label>

                  <select
                    value={filters.categoryId || ""}
                    onChange={handleCategoryChange}
                  >
                    {categories.map((cat) => (
                      <option key={cat.categoryId} value={cat.categoryId}>
                        {cat.categoryName}
                      </option>
                    ))}
                  </select>
                </div>



                <div>
                  <button
                    className="primary_btn"
                    onClick={fetchCategories}
                  >
                    <RefreshCw size={20} />
                  </button>
                </div>


                {(filters.categoryId && roleId === RoleId.Admin) && (
                  <>
                    <div>
                      <button
                        className="primary_btn"
                        onClick={handleEditCategory}
                      >
                        <SquarePen size={20} />
                      </button>
                    </div>
                    <div>
                      <button
                        className="primary_btn"
                        onClick={() => setIsTechnologyModalOpen(true)}
                      >
                        <Plus size={20} /> Add Technology
                      </button>
                    </div>
                    <div>
                      <button
                        className="primary_btn"
                        onClick={() => setIsCategoryModalOpen(true)}
                      >
                        <Plus size={20} /> Add Category
                      </button>
                    </div>
                    {selectedCategory.isActive ?
                      <div>
                        <button
                          className="primary_btn"
                          onClick={handleChangeVisibilityForCategories}
                        >
                          <CheckCircle size={20}  />
                        </button>
                      </div>
                      :
                      <div>
                        <button
                          className="primary_btn"
                          onClick={handleChangeVisibilityForCategories}
                        >
                          <Ban size={20} />
                        </button>
                      </div>
                    }
                  </>
                )}
              </div>
            </div>
          </div>

          {/* TABLE */}

          <div className="main_widget">
            <div className="main_widget_body">
              {techLoading ? (
                <LoadingSpinner />
              ) : (
                <div className="table-responsive mt-3">
                  <table>
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {technologies.length > 0 ? (
                        technologies.map((tech, index) => (
                          <tr key={tech.technologyId}>
                            <td>
                              {(filters.PageNo - 1) * filters.PageSize +
                                index +
                                1}
                            </td>

                            <td>{tech.technologyName}</td>

                            <td onClick={() => {
                              if (roleId === RoleId.Admin) {
                                handleChangeVisibilityForTechnologies(tech)
                              }
                            }
                            }>
                              {tech.isActive ? (
                                <CheckCircle className="icon icon-active" />
                              ) : (
                                <Ban className="icon icon-inactive" />
                              )}
                            </td>

                            <td onClick={() => {
                              if (roleId === RoleId.Admin) {
                                handleEditTechnology(tech)
                              }
                            }}>
                              <SquarePen className="icon icon-btn" />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4">No technologies found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* PAGINATION */}

      <Pagination
        currentPage={filters.PageNo}
        totalPages={totalPages}
        pageSize={filters.PageSize}
        onPageChange={(page) =>
          setFilters((prev) => ({ ...prev, PageNo: page }))
        }
        onPageSizeChange={(size) =>
          setFilters((prev) => ({
            ...prev,
            PageSize: size,
            PageNo: 1,
          }))
        }
      />

      {/* CATEGORY MODAL */}


      <div className={`modal_overlay ${isCategoryModalOpen ? "showModalBox" : ""}`}>
        <div className="modal">
          <div className="modal_header">
            <div className="modal_header_text">
              <h2>{isEditCategory ? "Edit Category" : "Add Category"}</h2>
            </div>
            <div className="modal_cross_icon" onClick={closeCategoryModal}>
              <i className="bi bi-x-lg"></i>
            </div>
          </div>

          <div className="modal_body">
            <div>
              <label className="label">Category Name</label>
              <input
                type="text"
                placeholder="Category Name"
                value={categoryName}
                className="form-control"
                onChange={(e) => setCategoryName(e.target.value)}
              />
            </div>

            <div className="modal-actions">
              {isEditCategory ? (processing ? (
                <div className="Publish_spinner"></div>
              ) : (
                <button className="delete_approve_btn" onClick={handleUpdateCategory}>
                  Save
                </button>
              )
              ) : processing ? (
                <div className="Publish_spinner"></div>
              ) : (
                <button className="delete_approve_btn" onClick={handleAddCategory}>
                  Save
                </button>
              )}
              <button className="delete_reject_btn" onClick={closeCategoryModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>


      {/* TECHNOLOGY MODAL */}


      <div className={`modal_overlay ${isTechnologyModalOpen ? "showModalBox" : ""}`}>
        <div className="modal">
          <div className="modal_header">
            <div className="modal_header_text">
              <h2>{isEditTechnology ? "Edit Technology" : "Add Technology"}</h2>
            </div>
            <div className="modal_cross_icon" onClick={closeTechnologyModal}>
              <i className="bi bi-x-lg"></i>
            </div>
          </div>


          <div className="modal_body">
            <div>
              <label className="label">Technology Name</label>
              <input
                type="text"
                placeholder="Technology Name"
                value={technologyName}
                className="form-control"
                onChange={(e) => setTechnologyName(e.target.value)}
              />
            </div>
            <div className="modal-actions">
              {isEditTechnology ? (
                processing ? (
                  <div className="Publish_spinner"></div>
                ) : (
                  <button
                    className="delete_approve_btn"
                    onClick={handleUpdateTechnology}
                    disabled={processing}
                  >
                    Save
                  </button>
                )
              ) : processing ? (
                <div className="Publish_spinner"></div>
              ) : (
                <button
                  className="delete_approve_btn"
                  onClick={handleAddTechnology}
                  disabled={processing}
                >
                  Save
                </button>
              )}
              <button className="delete_reject_btn" onClick={closeTechnologyModal}>
                Cancel
              </button>

            </div>
          </div>
        </div>
      </div>

    </>
  );
}

export default Technologies;
